import React, { useState } from 'react';
import { Image as ExpoImage, ImageProps, ImageLoadEventData } from 'expo-image';

interface ImageProp extends ImageProps {
  autoHeightFromWidth?: number;
  autoHeightMax?: number;
  autoWidthFromHeight?: number;
  autoWidthMax?: number;
  fitWidth?: number;
  fitHeight?: number;
}

export function computeFitSize(
  naturalWidth: number,
  naturalHeight: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return { width: maxWidth, height: maxHeight };
  }
  const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
  return {
    width: naturalWidth * scale,
    height: naturalHeight * scale,
  };
}

export default function Image({
  autoHeightFromWidth,
  autoHeightMax,
  autoWidthFromHeight,
  autoWidthMax,
  fitWidth,
  fitHeight,
  onLoad: onLoadProp,
  ...props
}: ImageProp): JSX.Element {
  const [natural, setNatural] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const onLoad = (e: ImageLoadEventData) => {
    setNatural({ width: e.source.width, height: e.source.height });
    onLoadProp?.(e);
  };

  let style: { width?: number; height?: number } = {};

  if (fitWidth && fitHeight) {
    style = natural
      ? computeFitSize(natural.width, natural.height, fitWidth, fitHeight)
      : { width: fitWidth, height: Math.min(fitWidth * 0.75, fitHeight) };
  } else if (autoHeightFromWidth) {
    const height = natural
      ? (natural.height / natural.width) * autoHeightFromWidth
      : autoHeightFromWidth * 0.75;
    style = {
      width: autoHeightFromWidth,
      height: autoHeightMax && height > autoHeightMax ? autoHeightMax : height,
    };
  } else if (autoWidthFromHeight) {
    const width = natural
      ? (natural.width / natural.height) * autoWidthFromHeight
      : autoWidthFromHeight * 0.75;
    style = {
      width: autoWidthMax && width > autoWidthMax ? autoWidthMax : width,
      height: autoWidthFromHeight,
    };
  }

  return <ExpoImage {...props} onLoad={onLoad} style={[style, props.style]} />;
}
